import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult, SilentRequest, SsoSilentRequest } from '@azure/msal-browser';
import { interval, Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { ENVIRONMENT } from '@ld.consumer-portal/models';
import { AuthService } from './auth.service';
import { BiometricService } from './biometric.service';
import { CiamAuthService } from './ciam-auth.service';
import { LoggingService } from './logging.service';
import { NativeCiamAuthService } from './native-ciam-auth.service';
import { PlatformService } from './platform.service';

@Injectable({
  providedIn: 'root',
})
export class TokenRefreshService {
  private readonly authService = inject(AuthService);
  private readonly msalService = inject(MsalService);
  private readonly ciamAuthService = inject(CiamAuthService);
  private readonly env = inject(ENVIRONMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly loggingService = inject(LoggingService);
  private readonly nativeCiamAuthService = inject(NativeCiamAuthService);
  private readonly platformService = inject(PlatformService);
  private readonly biometricService = inject(BiometricService);

  private readonly CHECK_INTERVAL_MS = 60 * 1000;

  private readonly REFRESH_BUFFER_SECONDS = 300;

  private readonly ENSURE_SESSION_TIMEOUT_MS = 10 * 1000;

  private readonly _isMonitoring = signal(false);
  readonly isMonitoring = this._isMonitoring.asReadonly();

  private readonly _isRefreshing = signal(false);
  readonly isRefreshing = this._isRefreshing.asReadonly();

  private readonly _nativeSessionResumed = new Subject<void>();
  readonly nativeSessionResumed = this._nativeSessionResumed.asObservable();

  private nativeResumeNotePending = false;
  private checkSubscription: Subscription | null = null;
  private waitIntervalId: ReturnType<typeof setInterval> | null = null;
  private waitTimeoutId: ReturnType<typeof setTimeout> | null = null;

  startMonitoring(): void {
    if (this._isMonitoring()) {
      return;
    }

    this._isMonitoring.set(true);

    this.checkSubscription = interval(this.CHECK_INTERVAL_MS)
      .pipe(
        filter(() => this.authService.isAuthenticated()),
        filter(() => !this._isRefreshing()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.checkAndRefresh();
      });

    if (this.isCiamMode ? this.ciamAuthService.isInitialized() : this.msalService.instance.getAllAccounts().length > 0) {
      this.checkAndRefresh();
    }
  }

  stopMonitoring(): void {
    this._isMonitoring.set(false);
    this.checkSubscription?.unsubscribe();
    this.checkSubscription = null;
    this.clearWaitTimers();
  }

  /**
   * Resolves whether the stored session can still be used.
   *
   * A token that merely exists is not enough: it survives in localStorage while the
   * tab is closed or hidden, so callers that resume an existing session (route guards,
   * `visibilitychange` handlers) must await this before rendering or refetching
   * borrower data.
   *
   * The refresh token only keeps an open page alive; it deliberately does not
   * resurrect a session whose access token has already lapsed. Access token lifetime
   * and the idle timeout are the two session controls, so an expired token resolves
   * `false` and the caller sends the user to login. A still-valid token is refreshed
   * only once it enters the renewal buffer. Never throws.
   */
  async ensureValidSession(): Promise<boolean> {
    if (!this.authService.isAuthenticated()) {
      return false;
    }

    if (this.authService.isTokenExpired()) {
      return false;
    }

    if (!this.authService.isTokenExpiringSoon(this.REFRESH_BUFFER_SECONDS)) {
      return true;
    }

    try {
      if (this.isCiamMode) {
        await this.ciamAuthService.ensureInitialized();
      }
      await this.withEnsureSessionTimeout(this.forceRefresh());
    } catch (error) {
      this.loggingService.logException(error, '[TokenRefreshService] Silent refresh failed for expiring session');
    }

    return !this.authService.isTokenExpired();
  }

  private withEnsureSessionTimeout(promise: Promise<void>): Promise<void> {
    let timerId: ReturnType<typeof setTimeout>;

    const timeout = new Promise<never>((_, reject) => {
      timerId = setTimeout(() => reject(new Error('Silent refresh timed out')), this.ENSURE_SESSION_TIMEOUT_MS);
    });

    return Promise.race([promise, timeout]).finally(() => clearTimeout(timerId));
  }

  async forceRefresh(): Promise<void> {
    if (!this.authService.isAuthenticated()) {
      throw new Error('Cannot refresh token: user is not authenticated');
    }

    if (this._isRefreshing()) {
      return this.waitForRefreshComplete();
    }

    if (this.isCiamMode) {
      await this.triggerCiamSilentRefresh();
    } else {
      await this.triggerMsalSilentRefresh();
    }
  }

  private waitForRefreshComplete(): Promise<void> {
    return new Promise((resolve) => {
      this.clearWaitTimers();

      this.waitIntervalId = setInterval(() => {
        if (!this._isRefreshing()) {
          this.clearWaitTimers();
          resolve();
        }
      }, 100);

      this.waitTimeoutId = setTimeout(() => {
        this.clearWaitTimers();
        resolve();
      }, 35000);
    });
  }

  private clearWaitTimers(): void {
    if (this.waitIntervalId !== null) {
      clearInterval(this.waitIntervalId);
      this.waitIntervalId = null;
    }
    if (this.waitTimeoutId !== null) {
      clearTimeout(this.waitTimeoutId);
      this.waitTimeoutId = null;
    }
  }

  private get isCiamMode(): boolean {
    return !!this.env.appSettings.useCustomerIdentityAuth;
  }

  private checkAndRefresh(): void {
    if (!this.authService.isAuthenticated() || this._isRefreshing()) {
      return;
    }

    if (this.authService.isTokenExpiringSoon(this.REFRESH_BUFFER_SECONDS)) {
      if (this.isCiamMode) {
        this.triggerCiamSilentRefresh();
      } else {
        this.triggerMsalSilentRefresh();
      }
    }
  }

  private async triggerMsalSilentRefresh(): Promise<void> {
    const oidcSettings = this.env?.appSettings?.oidcSettings;
    if (!oidcSettings?.client_id) {
      this.loggingService.logWarning('[TokenRefreshService] OIDC settings not configured');
      return;
    }

    this._isRefreshing.set(true);

    try {
      const activeAccount = this.msalService.instance.getActiveAccount();

      if (!activeAccount) {
        const accounts = this.msalService.instance.getAllAccounts();
          if (accounts.length === 0) {
            const msalKeys = Object.keys(localStorage).filter(k => k.includes('msal') || k.includes('login.windows.net'));
            this.loggingService.logWarning(
              `[TokenRefreshService] No MSAL accounts found, attempting ssoSilent recovery. ` +
              `MSAL-related localStorage keys: ${msalKeys.length}. ` +
              `isAuthenticated: ${this.authService.isAuthenticated()}`
            );
            const ssoResult = await this.attemptSsoSilentRecovery(oidcSettings);
            if (ssoResult) {
              this.storeRefreshResult(ssoResult);
            }
            return;
          }
        this.msalService.instance.setActiveAccount(accounts[0]);
      }

      const silentRequest: SilentRequest = {
        scopes: oidcSettings.scope?.split(' ') ?? ['openid', 'profile'],
        account: this.msalService.instance.getActiveAccount() ?? undefined,
        forceRefresh: true,
      };

      const result: AuthenticationResult = await this.msalService.instance.acquireTokenSilent(silentRequest);

      this.storeRefreshResult(result);

    } catch (error) {
      this.loggingService.logException(error, '[TokenRefreshService] MSAL silent refresh failed:');
    } finally {
      this._isRefreshing.set(false);
    }
  }

  private async attemptSsoSilentRecovery(
    oidcSettings: NonNullable<typeof this.env.appSettings.oidcSettings>,
  ): Promise<AuthenticationResult | null> {
    const loginHint = this.authService.getUserEmail();
    if (!loginHint) {
      this.loggingService.logWarning('[TokenRefreshService] No user email available for ssoSilent recovery');
      throw new Error('No MSAL accounts found and no loginHint available for ssoSilent');
    }

    this.loggingService.logWarning(`[TokenRefreshService] Attempting ssoSilent with loginHint: ${loginHint}`);

    const ssoRequest: SsoSilentRequest = {
      scopes: oidcSettings.scope?.split(' ') ?? ['openid', 'profile'],
      loginHint,
      authority: oidcSettings.authority,
    };

    const result = await this.msalService.instance.ssoSilent(ssoRequest);
    this.loggingService.logWarning('[TokenRefreshService] ssoSilent recovery succeeded');
    return result;
  }

  private storeRefreshResult(result: { accessToken?: string | null; idToken?: string | null; expiresOn?: Date | null }): void {
    if (result.accessToken) {
      this.authService.setToken(result.accessToken);
      if (this.platformService.isNativeMobile()) {
        this.authService.setCiamAccessToken(result.accessToken);
      }
    }
    if (result.idToken) {
      this.authService.setB2cIdToken(result.idToken);
    }
    if (result.expiresOn) {
      const expiresAt = Math.floor(result.expiresOn.getTime() / 1000);
      this.authService.setTokenExpiry(expiresAt);
    }
  }

  private async triggerCiamSilentRefresh(): Promise<void> {
    this._isRefreshing.set(true);

    try {
      const result = this.platformService.isNativeMobile()
        ? await this.nativeCiamAuthService.refreshToken()
        : await this.ciamAuthService.refreshToken();

      if (!result) {
        this.loggingService.logWarning('[TokenRefreshService] CIAM silent refresh returned null');
        return;
      }

      this.storeRefreshResult(result);

      if (this.platformService.isNativeMobile()) {
        this.notifyNativeSessionResumed();
      }
    } catch (error) {
      this.loggingService.logException(error, '[TokenRefreshService] CIAM silent refresh failed:');
    } finally {
      this._isRefreshing.set(false);
    }
  }

  /**
   * Records the outcome of the note triggered by `nativeSessionResumed`. Only a created note marks the
   * session as noted; a failed or skipped attempt stays retryable on the next refresh.
   */
  markNativeSessionResumeNoteHandled(noteCreated: boolean): void {
    this.nativeResumeNotePending = false;

    if (noteCreated) {
      sessionStorage.setItem('session_login_note_sent', 'true');
    }
  }

  private notifyNativeSessionResumed(): void {
    if (!this.biometricService.isBiometricEnabled()) {
      return;
    }

    if (
      this.nativeResumeNotePending ||
      sessionStorage.getItem('successful_login_note_sent') ||
      sessionStorage.getItem('session_login_note_sent')
    ) {
      return;
    }

    this.nativeResumeNotePending = true;
    this._nativeSessionResumed.next();
  }
}
